import React, {useCallback, useEffect, useRef, useState} from 'react';
import Alert from '../../Alert';
import {formatDateTime, newIdempotencyKey} from './newsletterState';

const PROVIDERS = {paypal: 'PayPal', authorizenet: 'Card', ccbill: 'CCBill'};
const STAGES = {
    created: 'Preparing payment', reserved: 'Preparing payment', 'provider-bound': 'Awaiting payment',
    submitting: 'Confirming payment', pending: 'Payment pending', unknown: 'Payment unconfirmed',
    held: 'Needs support review', disputed: 'Payment disputed', completed: 'Completed',
    reversed: 'Reversed', failed: 'Failed or cancelled',
};
const terminal = purchase => ['completed', 'reversed', 'failed'].includes(purchase.stage);
export const packPrice = pack => `${pack.currency} ${new Intl.NumberFormat(undefined, {
    style: 'currency', currency: pack.currency, currencyDisplay: 'narrowSymbol',
}).format(pack.amountMinor / 100)}`;

// The API admits rails by realm and merchant. Also restrict navigation/script execution locally.
export function purchaseApprovalUrl(purchase) {
    if (purchase.stage !== 'provider-bound') return null;
    try {
        const url = new URL(purchase.approvalUrl);
        const hosts = purchase.provider === 'paypal'
            ? ['www.paypal.com', 'www.sandbox.paypal.com']
            : purchase.provider === 'ccbill' ? ['api.ccbill.com'] : [];
        return url.protocol === 'https:' && hosts.includes(url.hostname)
            && !url.username && !url.password && !url.port ? url.href : null;
    } catch { return null; }
}

function HostedCardButton({purchase, busy, onToken, onError}) {
    const [ready, setReady] = useState(false);
    const handlers = useRef({onToken, onError});
    handlers.current = {onToken, onError};
    const callbackName = useRef(`newsletterCard${newIdempotencyKey().replace(/[^a-z0-9]/gi, '')}`);
    const config = purchase.acceptUi;
    useEffect(() => {
        if (!config?.clientKey || !config?.apiLoginId || ![
            'https://js.authorize.net/v3/AcceptUI.js',
            'https://jstest.authorize.net/v3/AcceptUI.js',
        ].includes(config.scriptUrl)) return;
        let submitted = false;
        const name = callbackName.current;
        window[name] = response => {
            if (submitted) return;
            const token = response?.opaqueData;
            if (response?.messages?.resultCode !== 'Ok'
                || typeof token?.dataDescriptor !== 'string' || typeof token?.dataValue !== 'string') {
                handlers.current.onError('Card details could not be confirmed. Please try again.');
                return;
            }
            submitted = true;
            setReady(false);
            // No card fields, complete SDK response, or nonce enter state/storage/logs.
            handlers.current.onToken({dataDescriptor: token.dataDescriptor, dataValue: token.dataValue});
        };
        // AcceptUI requires its button to exist before the script loads.
        const script = document.createElement('script');
        script.src = config.scriptUrl;
        script.onload = () => setReady(true);
        script.onerror = () => handlers.current.onError('Unable to load secure card entry. Refresh to retry.');
        document.body.appendChild(script);
        return () => { delete window[name]; script.remove(); };
    }, [purchase.uuid, config?.scriptUrl, config?.clientKey, config?.apiLoginId]);
    return <button type="button" className="button AcceptUI" disabled={busy || !ready}
        data-apiloginid={config?.apiLoginId} data-clientkey={config?.clientKey}
        data-billingaddressoptions='{"show":true,"required":false}'
        data-paymentoptions='{"showCreditCard":true,"showBankAccount":false}'
        data-acceptuiformbtntxt="Pay" data-acceptuiformheadertxt="Buy extra newsletter sends"
        data-responsehandler={callbackName.current}>Pay {packPrice(purchase)} by card</button>;
}

export default function NewsletterCreditsPanel({api, onRefresh}) {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [choice, setChoice] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [purchase, setPurchase] = useState(null);
    const [retry, setRetry] = useState(false);
    const active = useRef(true);
    const busyRef = useRef(false);
    const attempt = useRef(null);
    const requestVersion = useRef(0);
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);

    const load = useCallback(async () => {
        const version = ++requestVersion.current;
        try {
            const [balance, history, packs, purchases] = await Promise.all([
                api.credits(), api.creditHistory(), api.packs(), api.purchases(),
            ]);
            if (!active.current || version !== requestVersion.current) return;
            setData({balance: balance.data, history: history.data, packs: packs.data,
                purchases: purchases.data});
            setPurchase(current => current
                ? purchases.data.items.find(item => item.uuid === current.uuid) || current : null);
            setError('');
        } catch {
            if (active.current && version === requestVersion.current) {
                setError('Unable to refresh extra sends. Please try again.');
            }
        }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    async function run(action, isCreation = false) {
        if (busyRef.current || !active.current) return;
        busyRef.current = true;
        setBusy(true);
        setError('');
        try {
            const result = await action();
            if (!active.current) return;
            setPurchase(result.data);
            if (isCreation) {
                setRetry(false);
                attempt.current = null;
            }
            await load();
            if (active.current) onRefresh();
        } catch (err) {
            if (!active.current) return;
            if (err.data?.uuid) setPurchase(err.data);
            // Unknown create outcome: freeze key AND body until an explicit replay resolves it.
            const uncertain = !err.status || err.status >= 500 || [408, 429].includes(err.status);
            if (isCreation) {
                setRetry(Boolean(attempt.current) && uncertain);
                if (!uncertain) attempt.current = null;
            }
            setError(err.reason === 'newsletter-purchase-owner-required'
                ? 'Only the shop owner can buy extra sends.'
                : 'Payment is not confirmed. Refresh its status before starting another purchase. '
                    + (attempt.current ? 'Retry the same purchase to resolve this attempt.' : 'Contact support if it needs review.'));
        } finally {
            busyRef.current = false;
            if (active.current) setBusy(false);
        }
    }

    function begin() {
        if (!attempt.current) {
            const offer = offers.find(item => item.key === choice);
            if (!offer || !agreed) return;
            attempt.current = {key: newIdempotencyKey(), payload: {
                packId: offer.pack.id, packVersion: offer.pack.version,
                provider: offer.provider, termsVersion: offer.pack.termsVersion,
            }};
        }
        const saved = attempt.current;
        run(() => api.beginPurchase(saved.payload, saved.key), true);
    }

    async function more(kind) {
        if (busyRef.current || !data?.[kind].nextBeforeId) return;
        busyRef.current = true;
        setBusy(true);
        try {
            const result = await (kind === 'history' ? api.creditHistory : api.purchases)(data[kind].nextBeforeId);
            if (active.current) setData(current => ({...current, [kind]: {
                items: [...current[kind].items, ...result.data.items], nextBeforeId: result.data.nextBeforeId,
            }}));
        } catch {
            if (active.current) setError('Unable to load more history. Please try again.');
        } finally {
            busyRef.current = false;
            if (active.current) setBusy(false);
        }
    }

    const offers = (data?.packs || []).flatMap(pack => pack.providers
        .filter(provider => PROVIDERS[provider])
        .map(provider => ({pack, provider, key: JSON.stringify([pack, provider])})));
    const unfinished = data?.purchases.items.find(item => !terminal(item));
    const payment = purchase || unfinished;
    const approvalUrl = payment && purchaseApprovalUrl(payment);
    const selected = offers.find(item => item.key === choice);
    return <div className="nlm-credits" id="newsletter-extra-sends">
        <h3>Extra newsletter sends</h3>
        {error && <Alert type="error" role="alert">{error}</Alert>}
        {!data ? <p className="nlm-hint">Loading extra sends…</p> : <>
            <p><strong>{data.balance.available.toLocaleString()}</strong> extra recipient emails available.
                {' '}Gifted and purchased sends share one pool and never expire.
                Monthly allowance is used first. One credit emails one subscriber.</p>
            {!data.balance.spendingEnabled && <p className="nlm-hint">
                Extra-credit sending is paused. Your credits are retained.</p>}
            {data.balance.deficit > 0 && <Alert type="warning" role="status">
                A payment reversal left a {data.balance.deficit.toLocaleString()} credit shortfall.
                New credits cover it first. Your monthly allowance is still usable.
            </Alert>}
            {offers.length ? <fieldset className="nlm-pack-choices" disabled={busy || retry || Boolean(unfinished)}>
                <legend>Buy a send pack</legend>
                {offers.map(({pack, provider, key}) => <label className="checkbox" key={key}>
                    <input type="radio" name="newsletter-pack" value={key} checked={choice === key}
                        onChange={() => { setChoice(key); setAgreed(false); }} />
                    <span>{pack.quantity.toLocaleString()} recipient {pack.quantity === 1 ? 'email' : 'emails'} · {packPrice(pack)} · {PROVIDERS[provider]}</span>
                </label>)}
                <label className="checkbox">
                    <input type="checkbox" checked={agreed} onChange={event => setAgreed(event.target.checked)} />
                    <span>I understand this is a one-time purchase for this shop. No refunds.</span>
                </label>
            </fieldset> : <p className="nlm-hint">Send packs are not available for this shop yet.</p>}
            {(selected || retry) && <button type="button" className="button" disabled={busy || (!retry && (!agreed || Boolean(unfinished)))} onClick={begin}>
                {busy ? 'Please wait…' : retry ? 'Retry the same purchase' : `Continue · ${packPrice(selected.pack)}`}
            </button>}
            {payment && <div className="nlm-payment" aria-label="Selected payment">
                <h4>{STAGES[payment.stage] || 'Payment pending'}</h4>
                <p>{payment.quantity.toLocaleString()} recipient {payment.quantity === 1 ? 'email' : 'emails'} · {packPrice(payment)} · {PROVIDERS[payment.provider]}</p>
                {!terminal(payment) && <p className="nlm-hint">
                    Credits are added only after payment is verified. Leaving or cancelling at the
                    provider does not prove the attempt is unpaid. Contact support to release an abandoned payment.
                </p>}
                <div className="nlm-actions">
                    {approvalUrl && <a className="button" href={approvalUrl} target="_blank" rel="noopener noreferrer">Continue to {PROVIDERS[payment.provider]}</a>}
                    {payment.acceptUi && <HostedCardButton key={payment.uuid} purchase={payment} busy={busy}
                        onError={setError} onToken={token => run(() => api.completePurchase(payment.uuid, token))} />}
                    {payment.provider === 'paypal' && payment.stage === 'provider-bound' && <button type="button" className="button" disabled={busy}
                        onClick={() => run(() => api.completePurchase(payment.uuid))}>Finish approved PayPal payment</button>}
                    <button type="button" className="button secondary" disabled={busy}
                        onClick={() => run(() => api.purchase(payment.uuid))}>Refresh payment status</button>
                </div>
            </div>}
            <h4>Purchase history</h4>
            {!data.purchases.items.length && <p className="nlm-hint">No purchases yet.</p>}
            {data.purchases.items.map(item => <p key={item.uuid}>
                {formatDateTime(item.createdAt)} · {item.quantity.toLocaleString()} recipient {item.quantity === 1 ? 'email' : 'emails'} · {packPrice(item)} · {STAGES[item.stage] || 'Payment pending'}{' '}
                <button type="button" className="nlm-text-action" disabled={busy} onClick={() => run(() => api.purchase(item.uuid))}>View payment</button>
            </p>)}
            {data.purchases.nextBeforeId && <button type="button" className="button secondary" disabled={busy} onClick={() => more('purchases')}>Older purchases</button>}
            <div className="nlm-heading-actions">
                <h4>Credit history</h4>
                <button type="button" className="nlm-reload" disabled={busy}
                    aria-label="Refresh extra sends" title="Refresh extra sends"
                    onClick={() => { load(); onRefresh(); }}>
                    <i className="fas fa-sync-alt" aria-hidden="true" />
                </button>
            </div>
            {!data.history.items.length && <p className="nlm-hint">No credit activity yet.</p>}
            {data.history.items.map(item => <p key={item.uuid}>
                {formatDateTime(item.timestamp)} · {{grant: 'Gift', purchase: 'Purchase', send: 'Send reservation', cancel: 'Cancellation release', 'payment-adjustment': 'Payment adjustment'}[item.kind] || 'Credit adjustment'} · {item.delta > 0 ? '+' : ''}{item.delta.toLocaleString()} recipient emails
            </p>)}
            {data.history.nextBeforeId && <button type="button" className="button secondary" disabled={busy} onClick={() => more('history')}>Older credit activity</button>}
        </>}
        {!data && <button type="button" className="button secondary" disabled={busy}
            onClick={() => { load(); onRefresh(); }}>Try again</button>}
    </div>;
}
